import { NgClass, NgTemplateOutlet } from "@angular/common";
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    computed,
    effect,
    Input,
    OnInit,
    signal,
    viewChild,
    ViewEncapsulation,
} from "@angular/core";
import { FormsModule } from "@angular/forms";
import { TypeManagerDecorator } from "../../main/type.map.service";
import { Item } from "../../pojo/Item";
import { S25Const } from "../../util/s25-const";
import { Report } from "../../pojo/Report";
import { Bind } from "../../decorators/bind.decorator";
import { DocumentListItem, DocumentService } from "../../services/document.service";
import ObjectType = Report.ObjectType;
import Context = Report.Context;
import { EventMicroService } from "../../services/event.micro.service";
import { Organization } from "../s25-event/EventMicroI";
import { S25ReportComponent } from "../s25-report/s25.report.component";
import { Doc } from "../system-settings/document-management/s25.document.const";
import TypeId = Doc.TypeId;
import SimpleObject = Report.SimpleObject;
import { EventI } from "../../pojo/EventI";
import { S25DocumentManagementModule } from "../system-settings/document-management/s25.document.management.module";
import { S25DocusignEnvelopesComponent } from "./s25.docusign.envelopes.component";
import { IntegrationService, IntegrationTypes } from "../integrations/integration.service";
import { S25Util } from "../../util/s25-util";
import { s25Resource } from "../../util/s25.resource";
import { S25DropdownPaginatedModule } from "../s25-dropdown/s25.dropdown.paginated.module";
import { S25DropdownPaginatedComponent } from "../s25-dropdown/s25.dropdown.paginated.component";
import { DropDownItem } from "../../pojo/DropDownItem";
import { DropDownPaginatedModel } from "../../pojo/DropDownPaginatedModel";

@TypeManagerDecorator("s25-ng-print-object-report")
@Component({
    selector: "s25-ng-print-object-report",
    template: `
        <div class="moreActionsSection">
            @if (itemTypeId === Event) {
                <label class="c-margin-bottom--half">
                    <span class="c-margin-right--single">Choose your report type</span>
                    <select
                        [ngModel]="reportType()"
                        (ngModelChange)="onReportTypeChange($event)"
                        class="cn-form__control c-margin-top--quarter"
                    >
                        @for (type of reportTypes; track type) {
                            <option [value]="type.value">{{ type.label }}</option>
                        }
                    </select>
                </label>
            }
            @if (!reports?.length) {
                <div class="ngBold no-reports">No reports available for this {{ itemTypeName }}</div>
            }
            <div class="moreActionsPrint">
                <div class="moreActionsReports">
                    @switch (reportType()) {
                        @case ("reservation") {
                            <div class="reservationsContainer">
                                <div>
                                    <p class="ngBold c-margin-bottom--quarter">Select the Report Occurrence</p>
                                    <s25-ng-document-reservation-list
                                        class="ng-isolate-scope"
                                        [eventId]="itemId"
                                        (selected)="reservationChosen($event)"
                                    ></s25-ng-document-reservation-list>
                                </div>
                                <div class="c-margin-right--half">
                                    @if (reservationId()) {
                                        <p class="ngBold c-margin-bottom--quarter">Select Report to Run</p>
                                        @for (report of reservationReports; track report) {
                                            <s25-ng-report
                                                [report]="report"
                                                [context]="reportContext"
                                                [eventData]="eventData"
                                            ></s25-ng-report>
                                        }
                                    }
                                </div>
                            </div>
                        }
                        @case ("invoice") {
                            <div class="invoiceContainer">
                                <div>
                                    <p class="ngBold c-margin-bottom--quarter">Select the Report Invoice</p>
                                    <s25-ng-document-invoice-list
                                        [eventId]="itemId"
                                        (selected)="invoiceSelected($event)"
                                    ></s25-ng-document-invoice-list>
                                </div>
                                <div>
                                    @if (invoiceId()) {
                                        <ng-container *ngTemplateOutlet="orgDropdown"></ng-container>

                                        <p class="ngBold c-margin-bottom--quarter">Select Report to Run</p>
                                        @for (report of invoiceReports; track report) {
                                            <s25-ng-report
                                                [report]="report"
                                                [context]="reportContext"
                                                [eventData]="eventData"
                                            ></s25-ng-report>
                                        }
                                    }
                                </div>
                            </div>
                        }
                        @default {
                            <div
                                [ngClass]="{
                                    hasOrgReports: organizationReports?.length,
                                    moreSpace: objectReports?.length > 25,
                                }"
                            >
                                @for (report of objectReports; track report) {
                                    <s25-ng-report
                                        [report]="report"
                                        [context]="reportContext"
                                        [eventData]="eventData"
                                    ></s25-ng-report>
                                }
                            </div>

                            @if (organizationReports?.length) {
                                <div class="paramGroup">
                                    <ng-container *ngTemplateOutlet="orgDropdown"></ng-container>
                                    @for (report of organizationReports; track report) {
                                        <s25-ng-report
                                            [report]="report"
                                            [context]="reportContext"
                                            [eventData]="eventData"
                                        ></s25-ng-report>
                                    }
                                </div>
                            }
                        }
                    }
                </div>
            </div>
            @if (docusignReady() && itemTypeId === Event && signatureReports().length) {
                <div class="c-margin-top--single">
                    <div class="signature-document">
                        <label class="signature-document__label" [attr.id]="signatureDocumentLabelId">
                            Send a document for signature
                        </label>
                        <s25-dropdown-paginated
                            class="signature-document__select"
                            [model]="signatureDocumentModel"
                            [chosen]="chosenSignatureDocument()"
                            [onSelect]="chooseSignatureDocument"
                            [ariaLabel]="{ by: signatureDocumentLabelId }"
                            [scrollIntoView]="false"
                        ></s25-dropdown-paginated>
                    </div>
                    @if (selectedSignatureReport(); as report) {
                        @if (envelopeTarget(report); as target) {
                            <s25-docusign-envelopes
                                [documentId]="target.documentId"
                                [report]="report"
                                [context]="reportContext"
                                [objectTypeId]="target.typeId"
                                [objectId]="target.id"
                            ></s25-docusign-envelopes>
                        }
                    }
                </div>
            }
        </div>

        <ng-template #orgDropdown>
            <label
                >Choose Organization:
                <select
                    [ngModel]="organization()"
                    (ngModelChange)="setOrganization($event)"
                    class="cn-form__control c-margin-top--quarter"
                >
                    <option [ngValue]="null">All Organizations</option>
                    @for (org of organizations(); track org) {
                        <option [ngValue]="org.organizationId">
                            {{ org.organizationName }}
                        </option>
                    }
                </select>
            </label>
        </ng-template>
    `,
    styles: `
        .moreActionsPrint {
            padding-bottom: 1em;

            .moreActionsReports {
                display: flex;
                max-height: 200px;
                overflow: hidden;

                a:hover,
                a:focus {
                    text-decoration: none;
                }

                .reservationsContainer,
                .invoiceContainer {
                    display: grid;
                    grid-template-columns: 1fr 25rem;
                    width: 100%;
                }

                .reservationsContainer {
                    grid-template-columns: 1fr 30rem;
                }

                &:not(:has(.hasOrgReports)),
                .paramGroup,
                .hasOrgReports {
                    display: flex;
                    flex-direction: column;
                    flex-wrap: wrap;
                    column-gap: 1.5em;
                    overflow: auto;
                }

                .paramGroup {
                    border-left: 1px solid rgba(0, 0, 0, 0.15);
                    padding-left: 1.5em;
                    flex: 0 0 auto;

                    label {
                        margin-bottom: 5px;

                        select {
                            margin-top: unset !important;
                        }
                    }
                }

                .hasOrgReports.moreSpace {
                    flex: 1 1 auto;
                }

                &:has(.hasOrgReports) {
                    gap: 1.5em;
                }

                &:not(:has(.hasOrgReports)) {
                    column-gap: 3em;
                    overflow: auto;
                }
            }

            .reservationsContainer {
                s25-ng-report {
                    margin-left: 2em;
                    display: block;
                }
            }
        }

        .signature-document {
            display: flex !important;
            flex-direction: column !important;
            align-items: flex-start !important;
            width: 22rem !important;
            max-width: 100%;
            margin-bottom: 1.5rem;
        }

        .signature-document__label {
            display: block !important;
            margin: 0 0 1rem !important;
            padding: 0;
            font-weight: 700;
        }

        .signature-document__select {
            display: block !important;
            width: 22rem !important;
            min-width: 0 !important;
            max-width: 22rem !important;
            margin: 0 !important;
            box-sizing: border-box;
        }

        :host ::ng-deep .signature-document__select .ngDropdownPaginated,
        :host ::ng-deep .signature-document__select .ui-select-container,
        :host ::ng-deep .signature-document__select .select2-choice {
            width: 100% !important;
            max-width: 100% !important;
            min-width: 0 !important;
            box-sizing: border-box;
        }
    `,
    encapsulation: ViewEncapsulation.Emulated,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        FormsModule,
        S25DocumentManagementModule,
        S25DropdownPaginatedModule,
        S25ReportComponent,
        S25DocusignEnvelopesComponent,
        NgClass,
        NgTemplateOutlet,
    ],
})
export class S25PrintObjectReportComponent implements OnInit {
    @Input({ required: true }) itemId: number;
    @Input({ required: true }) itemTypeId: Item.Id;
    @Input() reports: Report.SimpleObject[] = [];

    hasReservationOption: boolean;
    hasInvoiceOption: boolean;
    hasOrganizationOption: boolean;
    itemTypeName: string;

    objectReports: Report.SimpleObject[] = [];
    invoiceReports: Report.SimpleObject[] = []; //Reports that accept an invoice (pricing set) id.
    reservationReports: Report.SimpleObject[] = [];
    organizationReports: Report.SimpleObject[] = []; //mostly standard pricing reports

    invoiceId = signal<number>(null);
    reservationId = signal<number>(null);

    reportType = signal<ReportTypes>("event");
    readonly selectedDocumentId = signal<number | null>(null);
    private readonly docusignConnectionEnabled = s25Resource({
        lazy: true,
        if: () => S25Const.docusignEnabled,
        loader: () => IntegrationService.getIntegrationEnabled(IntegrationTypes.docusign),
    });
    readonly docusignReady = computed(() => this.docusignConnectionEnabled.value() === true);

    readonly signatureDocumentLabelId = `signature-document-label-${++S25PrintObjectReportComponent.labelCount}`;
    readonly signatureDocumentModel: DropDownPaginatedModel = {
        items: [],
        itemNameProp: "itemName",
        searchEnabled: true,
        placeholder: "Choose a document",
        searchPlaceholder: "Filter documents",
        emptyText: "No documents",
        pageSize: 25,
    };
    private readonly signatureListVersion = signal(0);
    readonly signatureDocumentItems = computed(() => {
        this.signatureListVersion();
        this.organization();
        return this.signatureReports().map((report) => ({
            itemId: report.document_id,
            itemName: report.rpt_name,
        }));
    });
    readonly chosenSignatureDocument = computed(() => {
        const documentId = this.selectedDocumentId();
        if (documentId == null) return null;
        return this.signatureDocumentItems().find((item) => item.itemId === documentId) ?? null;
    });
    private readonly signatureDropdown = viewChild(S25DropdownPaginatedComponent);
    private static labelCount = 0;
    reportTypes: { value: ReportTypes; label: string }[] = [];
    reportContext: Context = {};
    documentByReportId: Map<number, DocumentListItem> = new Map();
    documentReports: Map<TypeId, DocumentListItem[]> = new Map();

    organizations = signal<Organization[]>([]);
    organization = signal<number | null>(null);

    eventData?: Partial<EventI> & {
        event_locator?: string;
        event_id?: number;
        event_name?: string;
        start_date?: string;
    };

    async getEventData() {
        const evData = await EventMicroService.getEventDetailById([this.itemId], true, ["organizations"]);
        const event = evData?.content?.data?.items?.[0] ?? {};

        this.eventData = {
            event_locator: event.eventLocator ?? "",
            event_id: event.id ?? 0,
            event_name: event.name ?? "",
            event_title: event.title ?? "",
            start_date: event.dates?.startDate ?? "",
        };

        this.organizations.update((orgs) => {
            return [...orgs, ...evData.content?.expandedInfo?.organizations];
        });
        this.cd.detectChanges();
    }

    constructor(private cd: ChangeDetectorRef) {
        effect(() => {
            const items = this.signatureDocumentItems();
            this.signatureDocumentModel.items = items;
            const dropdown = this.signatureDropdown();
            if (!dropdown) return;
            dropdown.setupItems(items);
            dropdown.currResults = [...items];
            dropdown.resetPagination(items.length);
        });
    }

    async ngOnInit() {
        this.itemTypeName = S25Const.itemId2Display[this.itemTypeId]?.singular;
        switch (this.itemTypeId) {
            //Historically, events and locations are the only object with obj specific reports
            case Item.Ids.Event:
                //Event object reports are event based, occurrence based, or invoice based
                await this.getDocumentReports();
                await this.completeReportsList();
                this.reportTypes = [{ value: "event", label: "Event Based" }];
                if (this.hasReservationOption)
                    this.reportTypes.push({ value: "reservation", label: "Occurrence Based" });
                if (this.hasInvoiceOption) this.reportTypes.push({ value: "invoice", label: "Invoice Based" });
                // Report runs take the event id in num_parm1.
                this.reportContext.num_parm1 = this.itemId;
                await this.getEventData();
                break;
            case Item.Ids.Location:
                //Locations have only ever had the daily room sheet JReport -147
                this.addObjectReports(this.reports);
                // The daily room sheet takes the location id in num_parm1.
                this.reportContext.num_parm1 = this.itemId;
                this.refreshSignatureDocuments();
                break;
        }
        this.cd.detectChanges();
    }

    async onReportTypeChange(newValue: ReportTypes) {
        if (newValue === "invoice" && !this.organizations()?.length) await this.getEventData();

        this.reportType.set(newValue);
        this.selectedDocumentId.set(null);
        this.cd.detectChanges();
    }

    async getDocumentReports() {
        const [contracts, orgContracts, rsrvContracts, invoiceDocuments] = await Promise.all([
            DocumentService.getDocumentList({ type: "event" }),
            DocumentService.getDocumentList({ type: "organization" }),
            DocumentService.getDocumentList({ type: "reservation" }),
            DocumentService.getDocumentList({ type: "invoice" }),
        ]);

        this.documentReports.set("event", contracts || []);
        this.documentReports.set("organization", orgContracts || []);
        this.documentReports.set("reservation", rsrvContracts || []);
        this.documentReports.set("invoice", invoiceDocuments || []);

        for (const contract of this.documentReports.get("event"))
            this.documentByReportId.set(contract.reportId, contract);
        for (const contract of this.documentReports.get("organization"))
            this.documentByReportId.set(contract.reportId, contract);
        for (const contract of this.documentReports.get("reservation"))
            this.documentByReportId.set(contract.reportId, contract);
        for (const doc of this.documentReports.get("invoice")) this.documentByReportId.set(doc.reportId, doc);
    }

    @Bind
    async completeReportsList() {
        const len = this.reports.length;
        for (let i = len - 1; i >= 0; i--) {
            let report = this.reports[i];
            const rptId = +report.rpt_id;
            this.addReportsFromShell(rptId, report);

            switch (+report.rpt_use) {
                case Report.Use.Confirmation:
                    report.rpt_name = "Default Confirmation: " + report.rpt_name;
                    break;
                case Report.Use.Invoice:
                    report.rpt_name = "Default Invoice: " + report.rpt_name;
                    break;
            }

            if (report.rpt_engine === "DM") {
                report = this.hydrateDocument(report);
                if (!report?.document_id) continue; //DM report w/o a valid document id is broken so skip it
            }
            this.addReportToGroup(report);
        }

        this.objectReports.sort(this.sortReports);
        this.reservationReports.sort(this.sortReports);
        this.invoiceReports.sort(this.sortReports);
        this.organizationReports.sort(this.sortReports);
        this.refreshSignatureDocuments();
    }

    private refreshSignatureDocuments() {
        this.signatureListVersion.update((version) => version + 1);
    }

    hydrateDocument(report: Report.SimpleObject) {
        const document = this.documentByReportId.get(report.rpt_id);
        if (!document) return report;

        return {
            ...report,
            rpt_name: document.itemName,
            document_id: document.itemId,
            optUUID: document.itemId,
            type: document.type,
        };
    }

    //report list is sorted by rpt_use then rpt_name, higher rpt_use first
    sortReports(a: Report.SimpleObject, b: Report.SimpleObject) {
        if (a?.rpt_use === b?.rpt_use) return a?.rpt_name?.localeCompare(b?.rpt_name);
        return b?.rpt_use - a?.rpt_use;
    }

    @Bind
    addReportToGroup(report: SimpleObject) {
        const parameter = this.documentParameter(report);
        if (parameter === "invoice") {
            //These accept an optional invoice id parameter
            this.addInvoiceReports([report]);
        } else if (parameter === "organization") {
            //These accept an optional organization id parameter
            this.addOrganizationReports([report]);
        } else if (parameter === "reservation") {
            //These accept an optional reservation id parameter
            this.addReservationReports([report]);
        } else {
            //no parameters besides objectId
            this.addObjectReports([report]);
        }
    }

    // Document-management rows are filed by object_type or by the document type string.
    // Signature sends use the same filing so an occurrence, invoice, or organization contract waits for that id.
    private documentParameter(report: SimpleObject): "invoice" | "organization" | "reservation" | null {
        if (report.object_type === ObjectType.PaymentDocument || report.type === Doc.type.invoice.id) {
            return "invoice";
        }
        if (report.object_type === ObjectType.OrganizationDocument || report.type === Doc.type.organization.id) {
            return "organization";
        }
        if (report.object_type === ObjectType.ReservationDocument || report.type === Doc.type.reservation.id) {
            return "reservation";
        }
        return null;
    }

    signatureReports(): Report.SimpleObject[] {
        if (this.itemTypeId !== Item.Ids.Event) return [];
        const type = this.reportType();
        const reports =
            type === "reservation"
                ? this.reservationReports
                : type === "invoice"
                  ? this.invoiceReports
                  : this.objectReports.concat(this.organizationReports);
        const seenDocumentIds = new Set<number>();
        const unique: Report.SimpleObject[] = [];
        for (const report of reports) {
            const documentId = this.finiteId(report.document_id);
            if (documentId == null || seenDocumentIds.has(documentId) || this.envelopeTarget(report) == null) {
                continue;
            }
            seenDocumentIds.add(documentId);
            unique.push(report);
        }
        return unique;
    }

    chooseDocument(value: number | string | null) {
        this.selectedDocumentId.set(this.finiteId(value));
    }

    chooseSignatureDocument = (item: DropDownItem | null) => {
        this.chooseDocument(item?.itemId ?? null);
    };

    selectedSignatureReport(): Report.SimpleObject | null {
        const documentId = this.selectedDocumentId();
        if (documentId == null) return null;
        return this.signatureReports().find((report) => report.document_id === documentId) ?? null;
    }

    envelopeTarget(report: Report.SimpleObject): { typeId: number; id: number; documentId: number } | null {
        const documentId = this.finiteId(report?.document_id);
        const eventId = this.finiteId(this.itemId);
        if (documentId == null || eventId == null || this.itemTypeId !== Item.Ids.Event) return null;
        const parameter = this.documentParameter(report);
        // Organization documents need the chosen organization, stored in num_parm4.
        if (parameter === "organization" && this.finiteId(this.reportContext.num_parm4) == null) return null;
        if (parameter === "reservation" && this.finiteId(this.reservationId()) == null) return null;
        if (parameter === "invoice" && this.finiteId(this.invoiceId()) == null) return null;
        return { typeId: Item.Ids.Event, id: eventId, documentId };
    }

    private finiteId(value: string | number | null | undefined): number | null {
        if (value == null || value === "") return null;
        const parsed = S25Util.parseInt(value);
        return Number.isFinite(parsed) ? parsed : null;
    }

    addReportsFromShell(rptId: number, report: Report.SimpleObject) {
        const reportTypes: { [key: number]: TypeId } = {
            [Report.Reports.EventShell.id]: "event",
            [Report.Reports.OrganizationShell.id]: "organization",
            [Report.Reports.ReservationShell.id]: "reservation",
            [Report.Reports.PaymentShell.id]: "invoice",
        };

        const objectTypes: { [key: number]: ObjectType } = {
            [Report.Reports.EventShell.id]: ObjectType.EventDocument,
            [Report.Reports.OrganizationShell.id]: ObjectType.OrganizationDocument,
            [Report.Reports.ReservationShell.id]: ObjectType.ReservationDocument,
            [Report.Reports.PaymentShell.id]: ObjectType.PaymentDocument,
        };

        const type: TypeId = reportTypes[rptId];
        const objectType: ObjectType = objectTypes[rptId];

        if (type && objectType) {
            const reports = this.documentReports.get(type)?.map((c) => ({
                ...report,
                rpt_id: c.reportId || rptId,
                rpt_name: `${type.charAt(0).toUpperCase() + type.slice(1)} Document: ${c.itemName}`,
                document_id: c.itemId,
                optUUID: c.itemId,
                object_type: objectType,
            }));

            reports.forEach(this.addReportToGroup);
        }
    }

    addObjectReports(reports: Report.SimpleObject[]) {
        if (!reports) return;
        this.objectReports.push(...reports);
    }

    addOrganizationReports(reports: Report.SimpleObject[]) {
        this.hasOrganizationOption = true;
        this.organizationReports.push(...reports);
    }

    addReservationReports(reports: Report.SimpleObject[]) {
        this.hasReservationOption = true;
        this.reservationReports.push(
            ...reports.map((report) => {
                return {
                    ...report,
                    type: Doc.type.reservation.id,
                };
            }),
        );
    }

    addInvoiceReports(reports: Report.SimpleObject[]) {
        this.hasInvoiceOption = true;
        this.invoiceReports.push(
            ...reports.map((report) => {
                return {
                    ...report,
                    type: Doc.type.invoice.id,
                };
            }),
        );
    }

    reservationChosen(reservationId: number) {
        // Occurrence documents take the chosen reservation id in char_parm4.
        this.reportContext.char_parm4 = reservationId?.toString();
        this.reservationId.set(reservationId);
        this.selectedDocumentId.set(null);
    }

    invoiceSelected(invoiceId: number) {
        // Invoice documents take the chosen invoice id in num_parm2.
        this.reportContext.num_parm2 = invoiceId?.toString();
        this.invoiceId.set(invoiceId);
        this.selectedDocumentId.set(null);
    }

    setOrganization(orgId: number | string) {
        const organizationId = this.finiteId(orgId);
        this.organization.set(organizationId);
        // Organization documents take the chosen organization id in num_parm4.
        this.reportContext.num_parm4 = organizationId == null ? undefined : String(organizationId);
        this.selectedDocumentId.set(null);
    }

    Event = Item.Ids.Event;
}

type ReportTypes = "reservation" | "event" | "invoice" | "organization";
